// Import calendar (Module B) — a place to upload the school calendar whenever it changes.
// Upload .xlsx or .csv, or paste the rows straight from Excel → a calm preview (bad dates are
// friendly prompts, not errors; entries already on the calendar are flagged) → import. The
// browser parses the file; POST /api/calendar/import does the validated, deduped bulk insert.
// Team-tier, factual: dates + events only. PDF + auto-scan are deferred (a future spec change).

import { api } from "../api";
import { esc } from "../dom";
import { parseDelimited, parseXlsx, type EntryType, type ParsedRow } from "../calendar-import";

const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MO = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function fmtDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  return `${WD[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${d} ${MO[m - 1]}`;
}
// Week number within the term (same convention as the calendar view).
function weekOf(date: string, startDate: string): number {
  const [sy, sm, sd] = startDate.split("-").map(Number) as [number, number, number];
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const days = Math.floor((Date.UTC(y, m - 1, d, 12) - Date.UTC(sy, sm - 1, sd, 12)) / 86400000);
  return Math.floor(days / 7) + 1;
}
const dedupeKey = (d: string, l: string, t: string): string => `${d}|${l.toLowerCase()}|${t}`;
const nEntries = (n: number): string => `${n} ${n === 1 ? "entry" : "entries"}`;

export async function renderCalendarImport(app: HTMLElement): Promise<void> {
  let termName = "";
  let startDate = "";
  let existing = new Set<string>();
  let rows: ParsedRow[] = [];
  let result: { added: number; skipped: number; invalid: number } | null = null;
  let msg: string | null = null;

  async function loadExisting(): Promise<void> {
    const data = await api<{
      term: { name: string; start_date: string } | null;
      entries: { entry_date: string; label: string; type: EntryType }[];
    }>("/api/calendar?scope=term");
    termName = data.term?.name ?? "";
    startDate = data.term?.start_date ?? "";
    existing = new Set(data.entries.map((e) => dedupeKey(e.entry_date, e.label, e.type)));
  }

  const isDup = (r: ParsedRow): boolean => existing.has(dedupeKey(r.entry_date, r.label, r.type));

  // ── preview rendering ──────────────────────────────────────────────────────────
  function previewHtml(): string {
    if (rows.length === 0) return "";
    const valid = rows.filter((r) => !r.error);
    const bad = rows.filter((r) => r.error);
    const dups = valid.filter(isDup);
    const toAdd = valid.filter((r) => !isDup(r));

    const rowHtml = (r: ParsedRow, tag: string): string =>
      `<div class="cal-row"><span class="cal-date">${esc(fmtDate(r.entry_date))}</span>` +
      `<span class="cal-label">${esc(r.label)}${r.note ? ` <span class="muted">· ${esc(r.note)}</span>` : ""}</span>` +
      `<span class="chip ${r.type}">${r.type}</span>${tag}</div>`;

    // Group the rows we'll add by week (systematic + aesthetic, like the calendar view).
    const byWeek = new Map<number, ParsedRow[]>();
    for (const r of toAdd) {
      const w = startDate ? weekOf(r.entry_date, startDate) : 0;
      (byWeek.get(w) ?? byWeek.set(w, []).get(w)!).push(r);
    }
    const addHtml = [...byWeek.keys()]
      .sort((a, b) => a - b)
      .map((w) => `<h2>${w <= 0 ? "Before term" : `Week ${w}`}</h2>${byWeek.get(w)!.map((r) => rowHtml(r, "")).join("")}`)
      .join("");

    const dupHtml = dups.length
      ? `<h2 class="muted">Already on the calendar</h2><p class="hint">These match entries you already have — they'll be skipped.</p>` +
        dups.map((r) => rowHtml(r, ` <span class="muted">· already added</span>`)).join("")
      : "";
    const badHtml = bad.length
      ? `<h2 class="muted">Needs a look</h2><p class="hint">We couldn't read these rows — they won't be imported. Fix them in the file, or add them by hand later.</p>` +
        bad.map((r) =>
          `<div class="cal-row"><span class="cal-date">${esc(r.entry_date || "—")}</span>` +
          `<span class="cal-label">${esc(r.label || "(no label)")}</span>` +
          `<span class="muted">${esc(r.error ?? "")}</span></div>`,
        ).join("")
      : "";

    const summary = `<p class="hint">${nEntries(toAdd.length)} to add` +
      `${dups.length ? ` · ${dups.length} already there` : ""}` +
      `${bad.length ? ` · ${bad.length} need a look` : ""}.</p>`;

    const importBtn = toAdd.length
      ? `<div style="height:8px"></div><button class="btn" id="import" type="button">Import ${nEntries(toAdd.length)}</button>`
      : `<p class="empty">Nothing new to import.</p>`;

    return `${summary}${addHtml}${dupHtml}${badHtml}${importBtn}`;
  }

  function msgHtml(): string {
    if (result) {
      const parts = [`Added ${result.added}.`];
      if (result.skipped) parts.push(`${result.skipped} were already there.`);
      if (result.invalid) parts.push(`${result.invalid} couldn't be read.`);
      return `<div class="toast">${esc(parts.join(" "))} <a data-link href="/calendar">View calendar</a></div>`;
    }
    if (msg) return `<p class="error">${esc(msg)}</p>`;
    return "";
  }

  function paint(): void {
    const m = app.querySelector<HTMLElement>("#msg");
    const p = app.querySelector<HTMLElement>("#previewout");
    if (m) m.innerHTML = msgHtml();
    if (p) p.innerHTML = previewHtml();
  }

  function render(): void {
    app.innerHTML = `
      <div class="topbar"><a data-link href="/calendar" class="muted">← Calendar</a></div>
      <h1>Import calendar</h1>
      <p class="hint">Upload the school calendar (.xlsx or .csv), or paste the rows from Excel.
        Columns: <strong>Date · What is it? · Type</strong>. ${esc(termName)}</p>
      <div id="importroot">
        <div class="card">
          <label for="calfile">Choose a file (.xlsx or .csv)</label>
          <input id="calfile" type="file" accept=".xlsx,.csv" />
          <label for="calpaste">…or paste rows from Excel</label>
          <textarea id="calpaste" placeholder="2026-06-08&#9;Term 3 start&#9;test&#10;2026-06-18&#9;Queen's Birthday&#9;holiday"></textarea>
          <div style="height:12px"></div>
          <button class="btn" id="preview" type="button">Preview</button>
        </div>
        <div id="msg">${msgHtml()}</div>
        <div id="previewout">${previewHtml()}</div>
      </div>`;

    app.querySelector<HTMLElement>("#importroot")!.addEventListener("click", onClick);
  }

  async function doPreview(): Promise<void> {
    msg = null;
    result = null;
    const fileInput = app.querySelector<HTMLInputElement>("#calfile");
    const ta = app.querySelector<HTMLTextAreaElement>("#calpaste");
    const file = fileInput?.files?.[0] ?? null;
    try {
      if (file) {
        rows = /\.xlsx$/i.test(file.name) ? await parseXlsx(await file.arrayBuffer()) : parseDelimited(await file.text());
      } else if (ta && ta.value.trim()) {
        rows = parseDelimited(ta.value);
      } else {
        msg = "Choose a file or paste some rows first.";
        rows = [];
      }
      if (file && rows.length === 0) msg = "That file had no rows we could read. Try pasting the cells from Excel instead.";
    } catch {
      msg = "Couldn't read that file. If it's an .xlsx that won't open, copy the cells from Excel and paste them instead.";
      rows = [];
    }
    paint();
  }

  async function doImport(): Promise<void> {
    const entries = rows.filter((r) => !r.error).map((r) => ({ entry_date: r.entry_date, label: r.label, type: r.type }));
    if (entries.length === 0) return;
    try {
      result = await api<{ added: number; skipped: number; invalid: number }>("/api/calendar/import", {
        method: "POST",
        body: JSON.stringify({ entries }),
      });
      rows = [];
      await loadExisting(); // refresh so a follow-up preview reflects what's now on the calendar
    } catch (e) {
      msg = (e as Error)?.message ?? "Something went wrong importing.";
    }
    paint();
  }

  function onClick(e: Event): void {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("button");
    if (!btn) return;
    if (btn.id === "preview") void doPreview();
    else if (btn.id === "import") void doImport();
  }

  await loadExisting();
  render();
}
